import type { ClosureReason, OpenDetail, PoolSection, PoolStatus, ReopenEstimate } from "../shared/types";
import { enrichClosedReport as postClosedEnrichment, submitReport } from "./api";
import { setStatus } from "./store";

/**
 * Submit a crowd report for a section with optimistic UI. The caller supplies a
 * `badge` element to reflect progress and callbacks for the resolved status and
 * the rate-limited path. An optional `reason` and `reopenEstimate` (only
 * meaningful for a closed report) and `openDetail` (only meaningful for an open
 * report) are threaded through to the API.
 *
 * Returns the written row's `ts` (`reportTs`) on a successful first-click WRITE
 * so the caller can stash it and later enrich that same row (see
 * `enrichClosedReport`); returns `undefined` when the report was rate-limited or
 * failed (offline) — the caller uses that to decide whether to reveal the
 * follow-up pickers.
 */
export async function reportSection(
  poolId: string,
  section: PoolSection,
  status: "open" | "closed",
  badge: HTMLElement,
  onStatus: (s: PoolStatus) => void,
  reason?: ClosureReason,
  reopenEstimate?: ReopenEstimate,
  openDetail?: OpenDetail,
): Promise<number | undefined> {
  // Detect whether the badge element uses the old pill-based `badge` class (splash
  // view, which hasn't yet adopted the D1 confidence-led tile style) vs the new
  // `status`-word style (pools list and detail). For the success path, `onStatus`
  // fires immediately and repaints the full tile, so style doesn't matter there.
  // For the error / rate-limit paths there is no `onStatus` call, so we must keep
  // the correct class family to avoid permanently stripping the pill styling.
  const isBadgePill = badge.classList.contains("badge");
  // Optimistic: reflect the user's report immediately in the status word. The
  // `badge` element is the section's `[data-role="status"]` word; a successful
  // report triggers `onStatus` → the view repaints the full tile (word + qual +
  // hero + accent), so this transient word is overwritten within a beat.
  badge.textContent = status === "open" ? "Open now…" : "Closed…";
  badge.className = isBadgePill
    ? `badge ${status === "open" ? "badge-open" : "badge-closed"}`
    : `status status-${status === "open" ? "open" : "closed"}`;

  try {
    // Reason + reopen estimate only ride along on a closed report; the
    // open-detail only on an open report (symmetric). Each is ignored otherwise.
    const res = await submitReport({
      poolId,
      section,
      status,
      reason: status === "closed" ? reason : undefined,
      reopenEstimate: status === "closed" ? reopenEstimate : undefined,
      openDetail: status === "open" ? openDetail : undefined,
    });
    if (res.ok && res.status) {
      setStatus(res.status);
      onStatus(res.status);
      return res.reportTs;
    } else if (!res.ok) {
      const secs = res.retryAfter && res.retryAfter > 0 ? res.retryAfter : 600;
      const at = new Date(Date.now() + secs * 1000);
      const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      badge.textContent = `Rate limit reached. You may update again at ${time}`;
      badge.className = isBadgePill ? "badge badge-unknown" : "status status-neutral";
    }
  } catch {
    // Offline / API down: keep the optimistic state so the report still reads.
    badge.textContent = status === "open" ? "Open now (offline)" : "Closed (offline)";
    badge.className = isBadgePill
      ? `badge ${status === "open" ? "badge-open" : "badge-closed"}`
      : `status status-${status === "open" ? "open" : "closed"}`;
  }
  return undefined;
}

/**
 * Enrich an already-committed CLOSED report (written by a first-click
 * `reportSection(..., "closed", ...)`) with the chosen reason/reopen estimate,
 * targeting the stashed `reportTs`. Fires NO new report and bumps NO rollup — the
 * closed STATUS already landed on the first tap. A no-op when both `reason` and
 * `reopenEstimate` are absent (Skip + Skip). Best-effort: failures are swallowed
 * because the durable closed status is already safe.
 */
export async function enrichClosedReport(
  poolId: string,
  section: PoolSection,
  targetTs: number,
  onStatus: (s: PoolStatus) => void,
  reason?: ClosureReason,
  reopenEstimate?: ReopenEstimate,
): Promise<void> {
  if (reason === undefined && reopenEstimate === undefined) return;
  try {
    const res = await postClosedEnrichment({ poolId, section, targetTs, reason, reopenEstimate });
    if (res.ok && res.status) {
      setStatus(res.status);
      onStatus(res.status);
    }
  } catch {
    // The closed status is already durable; losing the annotation is acceptable.
  }
}
